import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import Form from '../../../../common/Form';
import Input from '../../../../common/Form/Input';
import { useForm } from 'react-hook-form';
import { closeModalAction } from '../../../../actions/modal';
import { useDispatch, useSelector } from 'react-redux';
import { getEligibleAgents, broadcastTicketMilestone, acceptTicketMilestone, reAssignAgentToServiceReuest, assignHelpdesk, assignAgentsToTicketMilestone, getEligibleEngineers, releaseVendor, bulkAssignAgentsOfServiceRequest, bulkBroadcast } from '../../../../actions/service_request';
import IconToolTip from '../../../../common/IconToolTip';
import ButtonScogoClosedOutlined from '../../../../common/Buttons/ButtonScogoClosedOutlined';
import ButtonScogoPrimary from '../../../../common/Buttons/ButtonScogoPrimary';
import { SEND_TOAST } from '../../../../types/toast';
import { debounce, ScogoID } from '../../../../utils/common';
import AgentsList from './AgentsList';
import WorkersList from './WorkersList';
import { canBroadcastTicket, canModifyExecutionDate, isIndividualMilestoneStatusInRange, MilestoneSubStatus, TicketAgentRole } from '../../TicketController';
import moment from 'moment';
import MultiSelectDateTimePicker from '../../../../common/Form/MultiSelectDateTimePicker';
import { getStatusLabel, MilestoneSubStatusColor } from '../../TicketColumns/Status';
import RenderMilestones from '../../components/RenderMileStones';

export const formName = 'assign-ticket';

const showSpList = (newTicketaccept, ticket, newLoggedUserData) => {
    const agents = ticket.agents;
    if (newTicketaccept) return false;
    const currentUserRoles = agents.filter((a) => a.agent.id === newLoggedUserData.user.id).map((a) => a.role);
    const canAssign = [TicketAgentRole.SCM, TicketAgentRole.CLUSTER, TicketAgentRole.SUPERVISOR, TicketAgentRole.PSP].some((role) => currentUserRoles.includes(role));
    const isCustomer = ticket.org_id === newLoggedUserData.org.id;
    return canAssign || isCustomer;
};

const Assign = ({ ticket = {}, newTicketaccept = false, agent, onAccept = () => { }, callTicketDetails = false, isBulk = false, milestones = [], source }) => {
    const dispatch = useDispatch();
    const formButtonLoading = useSelector(state => state.loading.formButtonLoading);
    const eligibleAgents = useSelector(state => state.serviceRequest.eligibleAgents);
    const [selectedMilestones, setSelectedMilestones] = useState('');
    const [selectedAgent, setSelectedAgent] = useState(undefined);
    const [selectedWorker, setSelectedWorker] = useState(undefined);
    const newLoggedUserData = useSelector(state => state.auth.newLoggedUserData);
    const eligibleEngineers = useSelector(state => state.serviceRequest.eligibleEngineers);
    const [searchAgent, setSearchAgent] = useState('');

    const canChangeSp = useMemo(() => {
        if (isBulk) return true;
        return showSpList(newTicketaccept, ticket, newLoggedUserData);
    }, [newTicketaccept, newLoggedUserData, isBulk, ticket]);

    useEffect(() => {
        if (newTicketaccept && agent) {
            setSelectedAgent({ id: agent.id, full_name: agent.full_name });
        }
        if (!canChangeSp && source !== 'assign_ticket_to_user') {
            setSelectedAgent({ id: newLoggedUserData.user.id, full_name: newLoggedUserData.user.full_name });
        }
    }, [newTicketaccept, agent, canChangeSp, newLoggedUserData.user.id, newLoggedUserData.user.full_name, source]);

    const isReAssign = useMemo(() => {
        if (isBulk) return false;
        return ticket.agents && Array.isArray(ticket.agents) && ticket.agents.find((agent) => {
            return agent.role === TicketAgentRole.SP && agent.milestone_id === selectedMilestones
        });
    }, [selectedMilestones, ticket.agents, isBulk]);

    const memoizedMilestones = useMemo(() => {
        if (ticket.helpdesk_id !== ScogoID) {
            return ticket.milestones;
        }
        return ticket.milestones.filter((milestone) => {
            return isIndividualMilestoneStatusInRange(milestone, MilestoneSubStatus.NEW, MilestoneSubStatus.ENGINEER_LEFT_SITE)
        });
    }, [ticket.milestones, ticket.helpdesk_id]);

    useEffect(() => {
        if (!isBulk && memoizedMilestones.length > 0) {
            setSelectedMilestones(memoizedMilestones[0].id);
        }
    }, [memoizedMilestones, isBulk]);


    const getEligibleLAgentsList = useCallback(({ ticketId, limit, page, query, payload }) => {
        dispatch(getEligibleAgents({ ticketId, limit, page, query, payload }));
    }, [dispatch]);

    const debouncedAssignSearchSearch = useCallback(debounce(getEligibleLAgentsList), []);

    const onSearchAgent = useCallback((value) => {
        setSelectedAgent(undefined);
        setSearchAgent(value);
        debouncedAssignSearchSearch({ ticketId: ticket.id, limit: 100, page: 1, query: value });
    }, [debouncedAssignSearchSearch, ticket.id]);

    const getEligibleEngineersList = useCallback(({ user_id, limit, page, query }) => {
        dispatch(getEligibleEngineers({ user_id, limit, page, query }));
    }, [dispatch]);

    useEffect(() => {
        if ((canChangeSp && ticket.id && ticket.milestones && selectedMilestones)) {
            const dates = ticket.milestones.find(milestone => milestone.id === selectedMilestones).execution_dates.map(({ date }) => date);
            getEligibleLAgentsList({ ticketId: ticket.id, limit: 100, page: 1, query: '', payload: { execution_date: dates } });
        }
        if (isBulk) {
            getEligibleLAgentsList({ ticketId: ticket.id, limit: 100, page: 1, query: '', payload: {} });
        }
    }, [canChangeSp, getEligibleLAgentsList, ticket.id, ticket.milestones, selectedMilestones, isBulk]);

    useEffect(() => {
        if ((selectedAgent && selectedAgent.id && selectedAgent.id === newLoggedUserData.user.id) || (source === 'assign_ticket_to_user' && selectedAgent)) {
            getEligibleEngineersList({ user_id: selectedAgent.id });
        }
    }, [dispatch, selectedAgent, getEligibleEngineersList, newLoggedUserData.user.id, source]);

    const renderInfo = useMemo(() => (
        <IconToolTip title='The amount will be paid to partner for this ticket, after deducting the psp incentive if any.'>
            <span className='material-icons text-xl text-scogoprimary'>info</span>
        </IconToolTip>
    ), []);

    const memoizedAgents = useMemo(() => {
        const broadcast = { full_name: 'All (Broadcast in your network)', id: 'broadcast' };
        const assignedToMe = { full_name: `${newLoggedUserData.user.full_name} (Assign To Me)`, id: newLoggedUserData.user.id };
        const assignToScogo = { full_name: 'Assign To Scogo', id: ScogoID };
        let list = [];
        const findScogo = newLoggedUserData.orgMemberships.find(({ id }) => id === ScogoID);
        if (!findScogo && !isBulk) {
            list.push(assignToScogo);
        }

        if (!newTicketaccept && canBroadcastTicket(ticket)) {
            list.push(broadcast);
        }
        if (!isBulk) list.push(assignedToMe);
        if (eligibleAgents && eligibleAgents.length > 0) {
            list.push(...eligibleAgents);
        }
        if (selectedAgent && selectedAgent.id) {
            list = list.filter(agent => agent.id === selectedAgent.id);
        }
        list = Array.from(new Map(list.map(item => [item.id, item])).values());
        list = list.map(item => {
            if (item.id === newLoggedUserData.user.id) {
                return {
                    ...item,
                    full_name: `${newLoggedUserData.user.full_name} (Assign To Me)`
                }
            }
            return item;
        });
        return list;
    }, [eligibleAgents, newLoggedUserData.user.full_name, newLoggedUserData.user.id, selectedAgent, newLoggedUserData.orgMemberships, newTicketaccept, ticket, isBulk]);

    const memoizedWorkers = useMemo(() => {
        let list = [];
        if (eligibleEngineers && eligibleEngineers.length > 0) {
            list = eligibleEngineers.map((elem) => {
                if (elem.id === newLoggedUserData.user.id && source !== 'assign_ticket_to_user') {
                    return {
                        ...elem,
                        full_name: `${elem.full_name} (Assign To Me)`
                    }
                }
                if (source === 'assign_ticket_to_user' && selectedAgent && selectedAgent.id === elem.id) {
                    return {
                        ...elem,
                        full_name: `${elem.full_name} (Assign To Him)`
                    }
                }
                return elem;
            });
        }
        list = Array.from(new Map(list.map(item => [item.id, item])).values());
        return list;
    }, [eligibleEngineers, newLoggedUserData.user.id, source]);


    const handleAgentSelect = (agent) => {
        setSelectedAgent(selectedAgent && selectedAgent.id === agent.id ? undefined : agent);
    };

    const handleWorkersSelect = (engineer) => {
        setSelectedWorker(selectedWorker && selectedWorker.id === engineer.id ? undefined : engineer);
    };

    const methods = useForm({
        defaultValues: {},
        mode: 'all'
    });

    useEffect(() => {
        if (ticket && selectedMilestones) {
            const dates = ticket.milestones.find(milestone => milestone.id === selectedMilestones).execution_dates.map(({ date }) => new Date(date));
            if (dates.length > 0) {
                methods.setValue('dates', dates);
            } else {
                methods.setValue('dates', [new Date()]);
            }
        }
    }, [ticket, selectedMilestones, methods]);

    const showWorkersList = useMemo(() => {
        if (source === 'assign_ticket_to_user' && selectedAgent && eligibleEngineers?.length > 0) {
            return true;
        }
        return selectedAgent && selectedAgent.id === newLoggedUserData.user.id && eligibleEngineers?.length > 0;
    }, [selectedAgent, newLoggedUserData.user.id, eligibleEngineers, source]);


    const submit = (formValues) => {
        if (!selectedAgent) {
            dispatch({ type: SEND_TOAST, payload: { status: 'danger', message: "Please Select Service Partner" } });
            return;
        }

        if (isBulk && selectedAgent.id === 'broadcast') {
            const ids = milestones.map((elem) => elem.id);
            dispatch(bulkBroadcast({
                payload: {
                    remark: formValues.remark,
                    dates: formValues.dates ? formValues.dates.map(date => moment(date)) : [],
                    ids
                },
                formName
            }))
            return;
        }
        if (isBulk) {
            const payload = { agents: [] };
            if (formValues?.dates?.length > 0) {
                payload.dates = formValues.dates.map(date => moment(date));
            }
            milestones.forEach((milestone) => {
                payload.agents.push({
                    agent_id: selectedAgent.id,
                    role: "SP",
                    milestone_id: milestone.id,
                    removePrevAgent: true
                });
            });

            dispatch(bulkAssignAgentsOfServiceRequest({ payload, formName }));
            return;
        }

        if (onAccept && agent && newTicketaccept) {
            if (!selectedWorker) {
                dispatch({ type: SEND_TOAST, payload: { status: 'danger', message: "Please Select Engineer" } });
                return;
            }
            let agents = [
                { role: "FE", agent_id: selectedWorker.id },
                { role: "SP", agent_id: selectedAgent.id }
            ];
            onAccept(agents);
            return;
        }
        if (selectedAgent.id === ScogoID) {
            const payload = {
                helpdesk_id: selectedAgent.id,
                milestone_id: selectedMilestones,
            };
            dispatch(assignHelpdesk({ payload, formName, ticketId: ticket.id }));
        } else if (selectedAgent.id === 'broadcast') {
            const payload = {
                remark: formValues.remark,
                dates: formValues.dates ? formValues.dates.map(date => moment(date)) : [],
            };
            const findSP = ticket.agents.find(({ role, milestone_id }) => role === TicketAgentRole.SP && milestone_id === selectedMilestones);
            const findFE = ticket.agents.find(({ role, milestone_id }) => role === TicketAgentRole.FE && milestone_id === selectedMilestones);
            if (findSP && findFE) {
                dispatch(releaseVendor({
                    formName,
                    ticketId: ticket.id,
                    milestoneId: selectedMilestones,
                    closeModal: false,
                    refresh: false,
                    onSuccess: () => {
                        dispatch(broadcastTicketMilestone({ ticketId: ticket.id, milestoneId: selectedMilestones, payload, formName, callTicketDetails }));
                    }
                }));
            } else {
                dispatch(broadcastTicketMilestone({ ticketId: ticket.id, milestoneId: selectedMilestones, payload, formName }));
            }
        } else if (selectedAgent && selectedAgent.id === newLoggedUserData.user.id) {
            let agents = [];
            agents = [
                { role: "FE", agent_id: selectedWorker.id },
                { role: "SP", agent_id: selectedAgent.id }
            ];
            if (isReAssign) {
                dispatch(releaseVendor({
                    formName,
                    ticketId: ticket.id,
                    milestoneId: selectedMilestones,
                    closeModal: false,
                    refresh: false,
                    onSuccess: () => {
                        dispatch(acceptTicketMilestone({
                            ticketId: ticket.id,
                            milestoneIds: [selectedMilestones],
                            payload: { agents },
                            formName,
                            callTicketDetails
                        }));
                    }
                }));
            } else {
                dispatch(acceptTicketMilestone({
                    ticketId: ticket.id,
                    milestoneIds: [selectedMilestones],
                    payload: { agents },
                    formName,
                    callTicketDetails
                }));
            }

        } else if (isReAssign) {
            const sp = {
                agent_id: selectedAgent.id,
                role: "SP",
                removePrevAgent: true,
                milestone_id: selectedMilestones
            };
            if (!canChangeSp) {
                sp.agent_id = selectedWorker.id;
                sp.role = "FE";
            }
            const payload = {
                dates: formValues.dates ? formValues.dates.map(date => moment(date)) : [],
                agent: sp,
                remark: formValues.remark,
            }

            dispatch(reAssignAgentToServiceReuest({ ticketId: ticket.id, milestoneId: selectedMilestones, payload, formName, callTicketDetails }))

        } else {
            let agents = [
                {
                    agent_id: selectedAgent.id,
                    role: "SP",
                    milestone_id: selectedMilestones,
                    removePrevAgent: true
                }
            ];
            if (selectedWorker && selectedWorker.id) {
                agents.push({
                    agent_id: selectedWorker.id,
                    role: "FE",
                    milestone_id: selectedMilestones,
                    removePrevAgent: true
                });
            }
            const payload = {
                dates: formValues.dates,
                agent: {
                    agent_id: selectedAgent.id,
                    role: "SP",
                    milestone_id: selectedMilestones,
                    removePrevAgent: true
                },
                remark: formValues.remark,
            };
            dispatch(assignAgentsToTicketMilestone({ ticketId: ticket.id, milestoneId: selectedMilestones, payload, formName }));
        }
    };

    const isExectionDisabled = useMemo(() => {
        const findMile = ticket.milestones.find((elem) => elem.id === selectedMilestones);
        if (newTicketaccept && findMile?.execution_dates?.length > 0) return true;
        if (canModifyExecutionDate(ticket)) return false;
        return false;
    }, [ticket, newTicketaccept, selectedMilestones]);

    return (
        <div className='max-h-[65vh] px-4 bg-white rounded-md relative flex flex-col overflow-hidden'>
            <div className='flex-1 overflow-auto py-4'>
                <Form
                    methods={methods}
                    onSubmit={submit}
                    className="px-6 bg-white flex flex-col flex-1 overflow-auto"
                >
                    {!isBulk && memoizedMilestones.length > 0 && !newTicketaccept && (
                        <motion.div
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            className="rounded-lg px-2 pt-2"
                        >
                            <RenderMilestones
                                milestones={memoizedMilestones}
                                selectedMilestones={selectedMilestones}
                                setSelectedMilestones={setSelectedMilestones}
                            />
                        </motion.div>
                    )}

                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-3 gap-6"
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2 }}
                    >
                        {/* <MultiSelectDatePicker readOnly={isExectionDisabled} required name='dates' label='Execution Dates' minDate={new Date()} /> */}
                        <MultiSelectDateTimePicker readOnly={isExectionDisabled && !isBulk} required name='dates' label='Execution Dates' minDate={new Date()} />
                        <Input name='remark' label='Ticket Instructions' />
                        <Input
                            name='vendor_price'
                            label='Vendor Price'
                            renderInfo={renderInfo}
                            type='number'
                        />
                    </motion.div>

                    <div className="flex-1 flex flex-col gap-8 overflow-hidden mt-2">

                        {canChangeSp && <AgentsList
                            selectedAgent={selectedAgent}
                            handleAgentSelect={handleAgentSelect}
                            agents={memoizedAgents}
                            searchTerm={searchAgent}
                            setSearchTerm={onSearchAgent}
                            isReAssign={isReAssign}
                        />}

                        {showWorkersList && (
                            <WorkersList
                                eligibleWorkers={memoizedWorkers}
                                handleEngineerSelect={handleWorkersSelect}
                                selectedWorker={selectedWorker}
                            />
                        )}
                    </div>
                </Form>
            </div>
            <div className='flex justify-between py-4 gap-4 sticky bottom-0  z-10' style={{ height: '5rem' }}>
                {isReAssign && <div className='w-full'> <button
                    type="button"
                    className={`flex items-center gap-1 px-3 py-2 rounded-md transition-colors shadow-md ${formButtonLoading['release_vendor'] ? ' opacity-80 bg-gray-200 text-orange-700 cursor-not-allowed' : 'bg-orange-200 text-orange-700 hover:bg-orange-200'}`}
                    onClick={() => dispatch(releaseVendor({ formName: 'release_vendor', ticketId: ticket.id, milestoneId: selectedMilestones, closeModal: true }))}
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    disabled={formButtonLoading['release_vendor']}
                >
                    <span className="material-icons-outlined text-font11">inventory</span>
                    <span>Release Vendor</span>

                </button></div>}
                <motion.div
                    className="w-full flex justify-end space-x-4"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8 }}
                >
                    <ButtonScogoClosedOutlined
                        textOrComponent='Cancel'
                        onClick={() => dispatch(closeModalAction())}
                    />
                    <ButtonScogoPrimary
                        loading={formButtonLoading[formName]}
                        textOrComponent='Submit'
                        onClick={() => submit(methods.getValues())}
                    />
                </motion.div>
            </div>
        </div>
    );
};

export default Assign;